import { featureCollection, point } from "@turf/helpers";
import { Localization } from "@viamap/viamap2-common";
import { LayerInfo } from "src/common/managers/Types";
import { LayerFunc } from "src/managers/LayerFunc";
import {
  calculateEffectiveMarkSquareMeterPrice,
  mapSoilTypeToGroup,
} from "./JPBFunc";
import {
  JPBReferenceProperty,
  PriceMode,
  SOIL_QUALITY_GROUPS,
  SoilQualityGroup,
} from "./JPBState";

export type JPBExportToDataLayerOptions = {
  properties: JPBReferenceProperty[];
  priceMode: PriceMode;
  nonAgriToAgriRatio: number;
  /** Estimated unit price (DKK/m²) keyed by documentID. */
  estimatedPriceByDocumentId?: Map<string, number | null | undefined>;
  layerTitle?: string;
};

/** Percentile rank: share of values that are lower or equal, as 0–100. */
export function percentileRank(value: number, allValues: number[]): number {
  if (!Number.isFinite(value) || allValues.length === 0) return 0;
  const countLe = allValues.filter((v) => v <= value).length;
  return Math.round((countLe / allValues.length) * 100);
}

function getSoilGroupPercentages(
  prop: JPBReferenceProperty,
): { [group in SoilQualityGroup]?: number } {
  const percentages: { [group in SoilQualityGroup]?: number } = {};
  const jordbundskortOverlap = prop.overlapArea?.jordbundskortOverlap;
  if (!jordbundskortOverlap) return percentages;

  for (const [soilType, percentage] of Object.entries(jordbundskortOverlap)) {
    if (typeof percentage !== "number" || percentage <= 0) continue;
    const soilGroup = mapSoilTypeToGroup(soilType);
    if (!soilGroup) continue;
    percentages[soilGroup] = (percentages[soilGroup] ?? 0) + percentage;
  }
  return percentages;
}

function formatTransactionType(value: string): string {
  return value
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .replace(/\s+/g, " ")
    .trim();
}

function toIsoDate(date: Date | string | null | undefined): string | null {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

function getJordUnitPrice(prop: JPBReferenceProperty): number | null {
  return Number.isFinite(prop.pricePerSquareMeter) ? prop.pricePerSquareMeter : null;
}

function getMarkUnitPrice(
  prop: JPBReferenceProperty,
  nonAgriToAgriRatio: number,
): number | null {
  return calculateEffectiveMarkSquareMeterPrice(prop, nonAgriToAgriRatio);
}

function getEffectiveUnitPrice(
  prop: JPBReferenceProperty,
  priceMode: PriceMode,
  nonAgriToAgriRatio: number,
): number | null {
  if (priceMode === PriceMode.Landbrug) {
    return getMarkUnitPrice(prop, nonAgriToAgriRatio);
  }
  return getJordUnitPrice(prop);
}

function toHaPrice(unitPriceM2: number | null): number | null {
  return unitPriceM2 != null ? unitPriceM2 * 10000 : null;
}

function buildFeatureProperties(
  prop: JPBReferenceProperty,
  nonAgriToAgriRatio: number,
  estimatedPricePerM2: number | null | undefined,
  pricePercentile: number | null,
  areaPercentile: number | null,
  /** 1-based Bilag / report row number (#). */
  rowNumber: number | null,
): Record<string, string | number | null> {
  const prisPrM2Jord = getJordUnitPrice(prop);
  const prisPrM2Mark = getMarkUnitPrice(prop, nonAgriToAgriRatio);
  const soilPercentages = getSoilGroupPercentages(prop);

  const properties: Record<string, string | number | null> = {
    Nr: rowNumber,
    Handelsdato: toIsoDate(prop.transactionDate),
    Handelspris: Number.isFinite(prop.originalTotalPrice) ? prop.originalTotalPrice : null,
    Jordpris: Number.isFinite(prop.totalPrice) ? prop.totalPrice : null,
    Indeksforskel:
      prop.indexDifferencePct != null && Number.isFinite(prop.indexDifferencePct)
        ? prop.indexDifferencePct
        : null,
    Areal_m2: Number.isFinite(prop.area) ? prop.area : null,
    Areal_ha: Number.isFinite(prop.area) ? prop.area / 10000 : null,
    Pris_pr_m2_jord: prisPrM2Jord,
    Pris_pr_ha_jord: toHaPrice(prisPrM2Jord),
    Pris_pr_m2_mark: prisPrM2Mark,
    Pris_pr_ha_mark: toHaPrice(prisPrM2Mark),
    MarkOverlap:
      prop.overlapArea?.markOverlap != null && Number.isFinite(prop.overlapArea.markOverlap)
        ? prop.overlapArea.markOverlap
        : null,
    DokumentID: prop.documentID ?? null,
    BFE: prop.bfe_nr ?? null,
    Adresse: (prop.address ?? "").trim() || null,
    Handelstype: formatTransactionType(prop.transactionType || ""),
    Landbrugsnotering: prop.landbrugsnotering?.trim() || null,
    Estimeret_pris_pr_m2:
      estimatedPricePerM2 != null && Number.isFinite(estimatedPricePerM2)
        ? estimatedPricePerM2
        : null,
    Estimeret_pris_pr_ha:
      estimatedPricePerM2 != null && Number.isFinite(estimatedPricePerM2)
        ? estimatedPricePerM2 * 10000
        : null,
    Pris_driftsbygning:
      prop.prisDriftsbygning != null && Number.isFinite(prop.prisDriftsbygning)
        ? prop.prisDriftsbygning
        : null,
    Pris_stuehus:
      prop.prisStuehus != null && Number.isFinite(prop.prisStuehus)
        ? prop.prisStuehus
        : null,
    PrisPercentil: pricePercentile,
    ArealPercentil: areaPercentile,
  };

  for (const group of SOIL_QUALITY_GROUPS) {
    const key = group.replace(/\s+/g, "_");
    const pct = soilPercentages[group] ?? 0;
    properties[key] = pct > 0 ? pct : null;
  }

  return properties;
}

/**
 * Creates a Mapit point data layer from selected JPB reference transactions.
 * Numeric fields are exported as numbers (no currency/unit suffixes) so they can
 * be used for color-by-value / size-by-value styling. Both jordpris and markpris
 * are exported (Pris_pr_m2/ha_jord and Pris_pr_m2/ha_mark). `Nr` is the 1-based
 * Bilag row number (#) matching report map labels. Percentile properties
 * (PrisPercentil, ArealPercentil) rank each transaction within the exported set.
 *
 * Callers should pass checked properties in the same order as the report
 * (`referenceProperties` filtered by checked), so `Nr` stays aligned with Bilag.
 */
export function createJPBReferencePropertiesDataLayer(
  options: JPBExportToDataLayerOptions,
): LayerInfo | null {
  const {
    properties,
    priceMode,
    nonAgriToAgriRatio,
    estimatedPriceByDocumentId,
    layerTitle,
  } = options;

  // Assign Bilag-style 1-based numbers from the full checked list order before
  // dropping features without coordinates, so Nr matches report map labels.
  const rowNumberByDocumentId = new Map<string, number>();
  properties.forEach((prop, index) => {
    if (!rowNumberByDocumentId.has(prop.documentID)) {
      rowNumberByDocumentId.set(prop.documentID, index + 1);
    }
  });

  const exportable = properties.filter(
    (prop) =>
      prop.coordinates &&
      Number.isFinite(prop.coordinates.lat) &&
      Number.isFinite(prop.coordinates.lng),
  );
  if (exportable.length === 0) return null;

  const priceValues = exportable
    .map((prop) => getEffectiveUnitPrice(prop, priceMode, nonAgriToAgriRatio))
    .filter((v): v is number => v != null && Number.isFinite(v));
  const areaValues = exportable
    .map((prop) => prop.area)
    .filter((v): v is number => Number.isFinite(v));

  const features = exportable.map((prop) => {
    const unitPrice = getEffectiveUnitPrice(prop, priceMode, nonAgriToAgriRatio);
    const pricePercentile =
      unitPrice != null ? percentileRank(unitPrice, priceValues) : null;
    const areaPercentile = Number.isFinite(prop.area)
      ? percentileRank(prop.area, areaValues)
      : null;
    const estimated = estimatedPriceByDocumentId?.get(prop.documentID);

    return point(
      [prop.coordinates.lng, prop.coordinates.lat],
      buildFeatureProperties(
        prop,
        nonAgriToAgriRatio,
        estimated,
        pricePercentile,
        areaPercentile,
        rowNumberByDocumentId.get(prop.documentID) ?? null,
      ),
    );
  });

  const fc = featureCollection(features);
  const title =
    layerTitle ??
    Localization.getText("jpb:referenceTransactionsDataLayer");
  const layer = LayerFunc.createLayerInfoFromGeoJsonFeatureCollection(fc, title);

  const propertyNames = LayerFunc.extractPropertiesFromGeoJSON(fc);
  layer.propertiesInGeoJson = propertyNames;
  layer.propertiesToDisplay = propertyNames.map((name) => ({ name }));

  return layer;
}
